import axios from "axios";

// QA-08: an unset variable used to make API the string "undefined/api" and every call 404 silently. In development it now
// falls back to the local backend; in a production build it falls back to same-origin (an empty prefix), which is what a
// reverse proxy in front of both would serve. Set REACT_APP_BACKEND_URL (see .env.example) when the API is on another origin.
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? (process.env.NODE_ENV === "production" ? "" : "http://localhost:8001");
export const API = `${BACKEND_URL}/api`;

export const ADMIN_TOKEN_KEY = "vs_admin_token";

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  // Public bill endpoints are guest-accessible; never send the admin token to them (a stray or expired
  // admin session must not be able to break a customer-facing upload).
  // Same for the public newsletter endpoints: they use a signed token or a Turnstile token, never a session.
  if (typeof config.url === "string" && (config.url.startsWith("/bills/") || config.url.startsWith("/newsletter/"))) return config;
  const token = localStorage.getItem(ADMIN_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const fetchPlans = (params) => api.get("/plans", { params }).then(r => r.data);
export const fetchPlan = (id) => api.get(`/plans/${id}`).then(r => r.data);
export const fetchProviders = (params) => api.get("/providers", { params }).then(r => r.data);
export const fetchPlanCosts = (id) => api.get(`/plans/${id}/costs`).then(r => r.data);
export const fetchProvider = (id) => api.get(`/providers/${id}`).then(r => r.data);
export const fetchStates = () => api.get("/states").then(r => r.data);
export const resolveZip = (zip) => api.get(`/zip/${zip}`).then(r => r.data);
// Texas plans-page city-skyline banner: which of the 6 major TX metros a ZIP is nearest to
// (straight-line approximation -- see backend/catalog/nearest_city.py). city_key is null when
// the ZIP isn't within range of any of the 6, or on a malformed ZIP -- the caller (Plans.jsx)
// treats both the same way (neutral fallback, never a guess).
export const fetchNearestCity = (zip) => api.get("/nearest-city", { params: { zip } }).then(r => r.data);
// Street-address typeahead (Photon; see backend/addressauto/photon.py) for the ESID-lookup and
// homepage address fields. Below 3 characters the server returns an empty list without calling
// out to Photon; any upstream problem also fails soft to an empty list -- this is always safe to
// call, callers never need their own try/catch just to keep typing from breaking.
export const fetchAddressSuggestions = (q, signal) =>
  api.get("/address-autocomplete", { params: { q, limit: 6 }, signal }).then(r => r.data?.suggestions || []);
export const submitLead = (payload) => api.post("/leads", payload).then(r => r.data);
export const adminLogin = (email, password) => api.post("/admin/login", { email, password }).then(r => r.data);
export const fetchStats = () => api.get("/admin/stats").then(r => r.data);
export const fetchLeads = () => api.get("/leads").then(r => r.data);
export const updateLeadStatus = (id, payload) => api.patch(`/leads/${id}`, payload).then(r => r.data);
export const deleteLead = (id) => api.delete(`/leads/${id}`).then(r => r.data);
export const createProvider = (p) => api.post("/providers", p).then(r => r.data);
export const updateProvider = (id, p) => api.put(`/providers/${id}`, p).then(r => r.data);
export const deleteProvider = (id) => api.delete(`/providers/${id}`).then(r => r.data);
export const createPlan = (p) => api.post("/plans", p).then(r => r.data);
export const updatePlan = (id, p) => api.put(`/plans/${id}`, p).then(r => r.data);
export const deletePlan = (id) => api.delete(`/plans/${id}`).then(r => r.data);

// ---- Supplier plan feed (admin). The admin token is attached by the interceptor above. ----
export const PLAN_TEMPLATE_URL = `${API}/plan-feed/template.csv`;
export const fetchAdminPlans = (params) => api.get("/admin/plans", { params }).then(r => r.data);
export const setPlanActive = (id, active, reason) =>
  api.post(`/admin/plans/${id}/active`, { active, reason: reason || null }).then(r => r.data);
// The CSV file's text is sent as-is with Content-Type text/csv (the server also accepts JSON).
export const importPlanFeed = (providerId, csvText) =>
  api.post("/admin/plan-feed/import", csvText, {
    params: { provider_id: providerId },
    headers: { "Content-Type": "text/csv" },
    timeout: 60000,
  }).then(r => r.data);
export const fetchPlanSubmissions = (params) => api.get("/admin/plan-feed/submissions", { params }).then(r => r.data);
export const fetchPlanItems = (params) => api.get("/admin/plan-feed/items", { params }).then(r => r.data);
export const approvePlanItem = (id) => api.post(`/admin/plan-feed/items/${id}/approve`).then(r => r.data);
export const rejectPlanItem = (id, reason) =>
  api.post(`/admin/plan-feed/items/${id}/reject`, { reason: reason || null }).then(r => r.data);
export const fetchFeedSettings = (providerId) => api.get(`/admin/plan-feed/settings/${providerId}`).then(r => r.data);
export const saveFeedSettings = (providerId, values) =>
  api.put(`/admin/plan-feed/settings/${providerId}`, values).then(r => r.data);
export const fetchApiKeys = (providerId) => api.get(`/admin/plan-feed/${providerId}/api-keys`).then(r => r.data);
export const createApiKey = (providerId, label) =>
  api.post(`/admin/plan-feed/${providerId}/api-keys`, { label: label || "" }).then(r => r.data);
export const revokeApiKey = (keyId) => api.delete(`/admin/plan-feed/api-keys/${keyId}`).then(r => r.data);

// ---- Suppliers for admin pickers: every supplier, including ones the public list hides. ----
export const fetchAdminProviders = (params) => api.get("/admin/providers", { params }).then(r => r.data);

// ---- Waitlist (admin). Coming-soon-state signups: OH, PA, IL are currently unlinked from the public site but
// people already on the list, and future relaunches, still need this data visible. ----
export const fetchAdminWaitlist = (params) => api.get("/admin/waitlist", { params }).then(r => r.data);

// ---- Usage stats (admin). Anonymous counts of ESID lookups and bill checks (backend/usagestats/): totals plus a
// zero-filled 30-day UTC series. Nothing identifying is stored or returned. ----
export const fetchUsageStats = () => api.get("/admin/usage-stats").then(r => r.data);

// ---- Newsletter subscribers (admin). Searchable, filterable, paginated list: {items, total, page, page_size}. The CSV
// export (same filters) is a fetch-as-blob in lib/subscribersAdmin.js because a link cannot send the auth header. ----
export const fetchAdminSubscribers = (params) => api.get("/admin/newsletter/subscribers", { params }).then(r => r.data);

// ---- Social leads (admin). Reddit posts found by the 3-hourly scan job (socialoutreach/), each with an
// AI-drafted reply for the Owner to review and post manually -- nothing here ever posts to Reddit. ----
export const fetchSocialLeads = (params) => api.get("/admin/social-leads", { params }).then(r => r.data);
export const updateSocialLeadStatus = (id, status) =>
  api.patch(`/admin/social-leads/${id}`, { status }).then(r => r.data);

// ---- Enrollment reports (admin). Filters are the server's query names (see lib/reporting.js filterParams). ----
export const fetchReportDispositions = () => api.get("/admin/reports/dispositions").then(r => r.data);
export const fetchReportSummary = (params) => api.get("/admin/reports/enrollments/summary", { params }).then(r => r.data);
export const fetchReportList = (params) => api.get("/admin/reports/enrollments", { params }).then(r => r.data);
export const fetchReportDetail = (recordId) =>
  api.get(`/admin/reports/enrollments/${encodeURIComponent(recordId)}`).then(r => r.data);
export const fetchReportExports = (params) => api.get("/admin/reports/exports", { params }).then(r => r.data);
export const fetchReportAudit = (params) => api.get("/admin/reports/audit", { params }).then(r => r.data);
